// src/components/fees/StudentSelectionDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, TextField, Checkbox, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, Alert, CircularProgress, FormControlLabel, InputAdornment, Snackbar
} from '@mui/material';
import { Search as SearchIcon, Info as InfoIcon } from '@mui/icons-material';
import { feeAPI } from '../../services/api';

const StudentSelectionDialog = ({ open, onClose, onAllocate }) => {
  const [feeGroups, setFeeGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dueDate, setDueDate] = useState(new Date().toISOString().split('T')[0]);
  const [allocating, setAllocating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (open) {
      fetchFeeGroups();
    }
  }, [open]);

  useEffect(() => {
    if (selectedGroup) {
      fetchStudents();
    }
  }, [selectedGroup]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = students.filter(s => 
        s.registration_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.class_stream_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredStudents(filtered);
    } else {
      setFilteredStudents(students);
    }
  }, [searchTerm, students]);

  const fetchFeeGroups = async () => {
    try {
      const response = await feeAPI.getGroups({ per_page: 1000 });
      const groups = Array.isArray(response.data) ? response.data : response.data.data || [];
      setFeeGroups(groups);
    } catch (error) {
      console.error('Error fetching fee groups:', error);
    }
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await feeAPI.getStudentsForAllocation({ fee_group_id: selectedGroup.id });
      setStudents(response.data.students || []);
      setFilteredStudents(response.data.students || []);
      
      // Auto-select students who haven't been allocated yet
      const notAllocated = (response.data.students || [])
        .filter(s => !s.already_allocated)
        .map(s => s.id);
      setSelectedStudents(notAllocated);
    } catch (error) {
      console.error('Error fetching students:', error);
      setStudents([]);
      setFilteredStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGroupChange = (e) => {
    const groupId = e.target.value;
    const group = feeGroups.find(g => g.id == groupId);
    setSelectedGroup(group);
    setSelectedStudents([]);
    setSearchTerm('');
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const notAllocatedIds = filteredStudents
        .filter(s => !s.already_allocated)
        .map(s => s.id);
      setSelectedStudents(notAllocatedIds);
    } else {
      setSelectedStudents([]);
    }
  };

  const handleSelectStudent = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleAllocate = async () => {
    if (selectedStudents.length === 0) {
      setSnackbar({ open: true, message: 'Please select at least one student', severity: 'warning' });
      return;
    }

    setAllocating(true);
    try {
      const response = await feeAPI.bulkAllocateToStudents({
        fee_group_id: selectedGroup.id,
        student_ids: selectedStudents,
        due_date: dueDate
      });

      setSnackbar({ open: true, message: response.data.message || 'Fees allocated successfully!', severity: 'success' });
      
      if (onAllocate) {
        onAllocate();
      }
      
      handleClose();
    } catch (error) {
      console.error('Error allocating fees:', error);
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to allocate fees', severity: 'error' });
    } finally {
      setAllocating(false);
    }
  };

  const handleClose = () => {
    setSelectedGroup(null);
    setStudents([]);
    setFilteredStudents([]);
    setSelectedStudents([]);
    setSearchTerm('');
    setDueDate(new Date().toISOString().split('T')[0]);
    onClose();
  };

  const notAllocatedStudents = filteredStudents.filter(s => !s.already_allocated);
  const allNotAllocatedSelected = notAllocatedStudents.length > 0 && 
    notAllocatedStudents.every(s => selectedStudents.includes(s.id));

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Bulk Allocate Fees to Students</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {/* Fee Group Selection */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Select Fee Group
            </Typography>
            <select
              value={selectedGroup?.id || ''}
              onChange={handleGroupChange}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '14px',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }}
            >
              <option value="">-- Select a fee group --</option>
              {feeGroups.map(group => (
                <option key={group.id} value={group.id}>
                  {group.name} - UGX {group.total_amount?.toLocaleString()} ({group.term?.name} {group.term?.year})
                </option>
              ))}
            </select>
          </Box>

          {selectedGroup && (
            <>
              {/* Fee Group Info */}
              <Alert severity="info" sx={{ mb: 3 }} icon={<InfoIcon />}>
                <Typography variant="body2" fontWeight={600}>
                  {selectedGroup.name}
                </Typography>
                <Typography variant="body2">
                  Total Amount: UGX {selectedGroup.total_amount?.toLocaleString()}
                </Typography>
                <Typography variant="body2">
                  Fee Types: {selectedGroup.fee_types?.map(ft => ft.name).join(', ')}
                </Typography>
                <Typography variant="body2">
                  Classes: {selectedGroup.classes?.map(c => c.name).join(', ')}
                </Typography>
              </Alert>

              {/* Due Date */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Due Date"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              {/* Search */}
              <TextField
                fullWidth
                placeholder="Search by name, registration no, or class..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Student List */}
              {loading ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allNotAllocatedSelected}
                          onChange={handleSelectAll}
                          disabled={notAllocatedStudents.length === 0}
                        />
                      }
                      label={`Select All (${selectedStudents.length} of ${notAllocatedStudents.length} selected)`}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Total students found: {filteredStudents.length}
                    </Typography>
                  </Box>

                  <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={allNotAllocatedSelected}
                              onChange={handleSelectAll}
                              disabled={notAllocatedStudents.length === 0}
                            />
                          </TableCell>
                          <TableCell>Reg No</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Class</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredStudents.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              No students found
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredStudents.map((student) => (
                            <TableRow 
                              key={student.id} 
                              hover
                              sx={{ 
                                opacity: student.already_allocated ? 0.5 : 1,
                                bgcolor: student.already_allocated ? '#f5f5f5' : 'transparent'
                              }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedStudents.includes(student.id)}
                                  onChange={() => handleSelectStudent(student.id)}
                                  disabled={student.already_allocated}
                                />
                              </TableCell>
                              <TableCell>{student.registration_no}</TableCell>
                              <TableCell>{student.full_name}</TableCell>
                              <TableCell>{student.class_stream_name}</TableCell>
                              <TableCell>
                                {student.already_allocated ? (
                                  <Chip label="Already Allocated" size="small" color="default" />
                                ) : (
                                  <Chip label="Not Allocated" size="small" color="success" variant="outlined" />
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={handleAllocate} 
          variant="contained"
          disabled={!selectedGroup || selectedStudents.length === 0 || allocating}
        >
          {allocating ? 'Allocating...' : `Allocate to ${selectedStudents.length} Student(s)`}
        </Button>
      </DialogActions>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default StudentSelectionDialog;